import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api } from '../../lib/api';
import { useUrlPrefix } from '../../lib/useUrlPrefix';
import { MeetingTypeBadge, offeredModes } from '../../lib/meetingType';

type Service = {
  id: string;
  name: string;
  slug: string;
  duration_minutes: number;
  offers_in_person: number;
  offers_phone: number;
  offers_video: number;
  description: string | null;
};

type LocationWithServices = {
  id: string;
  name: string;
  slug: string;
  address: string;
  timezone: string;
  is_virtual: number;
  services: Service[];
};

export default function LocationDetail() {
  const { orgSlug, locSlug } = useParams();
  const prefix = useUrlPrefix();
  const [data, setData] = useState<LocationWithServices | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!orgSlug || !locSlug) return;
    api<LocationWithServices>(`/book/${orgSlug}/locations/${locSlug}`)
      .then(setData)
      .catch((e) => setErr(e.message || 'load_failed'));
  }, [orgSlug, locSlug]);

  if (err === 'not_found') return <h1>Location not found</h1>;
  if (err) return <p className="err">Error: {err}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  return (
    <div>
      <div className="book-context">
        <Link to={`${prefix}/${orgSlug}/locations`} className="btn ghost small">
          <ArrowLeft size={12} /> Choose a different location
        </Link>
      </div>

      <h1>{data.name}</h1>
      <p className="muted">{data.is_virtual ? 'Online — meeting details are in your booking confirmation.' : data.address}</p>

      <h2>Pick a service</h2>
      {data.services.map((s) => (
        <div className="card" key={s.id}>
          <div className="row">
            <div>
              <strong>{s.name}</strong>{' '}
              <span className="muted">· {s.duration_minutes} min</span>{' '}
              {offeredModes(s).map((m) => (
                <MeetingTypeBadge key={m} type={m} />
              ))}
              {s.description && <p className="muted" style={{ margin: '0.25rem 0 0' }}>{s.description}</p>}
            </div>
            <div className="spacer" />
            <Link
              to={`${prefix}/${orgSlug}/locations/${locSlug}/services/${s.slug}`}
              className="btn primary"
            >
              Choose
            </Link>
          </div>
        </div>
      ))}
      {data.services.length === 0 && (
        <p className="muted">
          No services offered at this location yet. Please check back later or
          try a different location.
        </p>
      )}
    </div>
  );
}
